import { Injectable } from "@angular/core";
import {
  BlobServiceClient,
  ContainerClient,
  BlockBlobClient,
} from "@azure/storage-blob";
import { DefaultAzureCredential } from "@azure/identity";

export interface UploadConfig {
  containerName: string;
  connectionString?: string;
  sasToken?: string;
  accountName?: string;
  baseUrl?: string;
  file?: Blob | File;
  complete?: () => void;
  error?: (err: any) => void;
  progress?: (percent: number) => void;
  sas?: string;
}

export interface UploadParams {
  name: string;
  content: Blob | File;
  onProgress?: (progress: number) => void;
  sas?: string;
  containerName?: string;
  storageAccount?: string;
}

@Injectable({
  providedIn: "root",
})
export class BlobService {
  private blobServiceClient: BlobServiceClient | null = null;

  constructor() {}

  private getBlobServiceClient(config: UploadConfig): BlobServiceClient {
    if (this.blobServiceClient) {
      return this.blobServiceClient;
    }

    if (config.connectionString) {
      this.blobServiceClient = BlobServiceClient.fromConnectionString(
        config.connectionString
      );
    } else if (config.sasToken && config.accountName) {
      const accountUrl = `https://${config.accountName}.blob.core.windows.net`;
      this.blobServiceClient = new BlobServiceClient(
        `${accountUrl}${config.sasToken}`
      );
    } else {
      // Use DefaultAzureCredential for authentication
      const accountUrl = `https://${config.accountName}.blob.core.windows.net`;
      this.blobServiceClient = new BlobServiceClient(
        accountUrl,
        new DefaultAzureCredential()
      );
    }

    return this.blobServiceClient;
  }

  async uploadToBlobStorage(
    config: UploadConfig,
    params: UploadParams
  ): Promise<string> {
    try {
      const blobServiceClient = this.getBlobServiceClient(config);
      const containerClient = blobServiceClient.getContainerClient(
        config.containerName
      );

      // Create container if it doesn't exist
      await containerClient.createIfNotExists();

      const blockBlobClient = containerClient.getBlockBlobClient(params.name);

      // Upload the blob
      const uploadResponse = await blockBlobClient.uploadData(params.content, {
        onProgress: (progress) => {
          if (params.onProgress) {
            // Calculate progress based on loaded bytes vs content size
            const contentSize = params.content.size;
            const progressPercent = (progress.loadedBytes / contentSize) * 100;
            params.onProgress(progressPercent);
          }
        },
      });

      return blockBlobClient.url;
    } catch (error) {
      console.error("Error uploading to blob storage:", error);
      throw error;
    }
  }

  async deleteBlob(config: UploadConfig, blobName: string): Promise<void> {
    try {
      const blobServiceClient = this.getBlobServiceClient(config);
      const containerClient = blobServiceClient.getContainerClient(
        config.containerName
      );
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      await blockBlobClient.delete();
    } catch (error) {
      console.error("Error deleting blob:", error);
      throw error;
    }
  }

  async listBlobs(config: UploadConfig): Promise<string[]> {
    try {
      const blobServiceClient = this.getBlobServiceClient(config);
      const containerClient = blobServiceClient.getContainerClient(
        config.containerName
      );

      const blobs: string[] = [];
      for await (const blob of containerClient.listBlobsFlat()) {
        blobs.push(blob.name);
      }

      return blobs;
    } catch (error) {
      console.error("Error listing blobs:", error);
      throw error;
    }
  }

  generateBlobUrl(config: UploadConfig, fileName: string): string {
    if (config.baseUrl) {
      return `${config.baseUrl}/${fileName}`;
    }

    if (config.accountName) {
      return `https://${config.accountName}.blob.core.windows.net/${config.containerName}/${fileName}`;
    }

    throw new Error("Cannot generate blob URL: missing baseUrl or accountName");
  }

  async upload(config: UploadConfig): Promise<void> {
    if (!config.file) {
      throw new Error("File is required for upload");
    }

    try {
      const fileName = config.file instanceof File ? config.file.name : "blob";
      const uploadParams: UploadParams = {
        name: fileName,
        content: config.file,
        onProgress: config.progress,
      };

      const url = await this.uploadToBlobStorage(config, uploadParams);

      if (config.complete) {
        config.complete();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      if (config.error) {
        config.error(error);
      } else {
        throw error;
      }
    }
  }
}
